import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../../../components/custom-table/custom-table";
import DocumentScreenshotViewerModal from "../../../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import ImageViewerWithBadgesModal from "../../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import RadioSelectModal from "../../../../../components/modals/radioSelectModal/radioSelectModal";
import ViewReviewModal from "../../../../../components/modals/ViewReviewModal/ViewReviewModal";
import { DOCUMENT_SCREENSHOT_VIEWER_TITLE } from "../../../../../constants/stringLiterals";
import {
  FilterSection,
  FilterValues,
} from "../../../../../data-model/filterValues";
import { isHealthFacilityFeatureEnabled } from "../../../../../data-model/healthFacility";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { ModalId } from "../../../../../enums/modalId";
import { isExceptionFlowForUrologyOrg } from "../../../../../enums/organizationTypes";
import { TableNames } from "../../../../../enums/tableNames";
import { useExportTableData } from "../../../../../hooks/useExportTableData";
import { useReviewIconClick } from "../../../../../hooks/useReviewIconClick";
import { setFilter } from "../../../../../redux/slice/filterValuesSlice";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { adjustTableHeadersForReviewMode } from "../../../../../utils/adjustTableHeadersForReviewMode";
import { handleDocUploadScreenshotViewForOrdersForNewModalDesign } from "../../../../../utils/handleDocUploadScreenshot";
import { getMedicalPaOrderRowTooltip } from "../../../../../utils/medicalPaOrderRowTooltip";
import { handlePropagateEventOnClickInRpaStatusCell } from "../../../../../utils/medicalPaOrdersTableHandlers";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { refreshPostgresFacetCounts } from "../../../../../utils/refreshPostgresFacetCounts";
import { swapUnderscoreHyphen } from "../../../../../utils/swapUnderscoreWithHypen";
import { useFilterOptionsGeneratorFromAlgolia } from "../../../hooks/useFilterOptionsGeneratorFromAlgolia";
import { useBoStatusRadioSelect } from "../../../medicalPaOrdersAlgolia/hooks/useBoStatusRadioSelect";
import { applySunstateUrologyFilterOverrides } from "../../../medicalPaOrdersAlgolia/utils/applySunstateUrologyFilterOverrides";
import { useFilterAndSearch } from "../../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../../hooks/useInitialDataFetchingForTable";
import { useModalOpenerForMyPaOrders } from "../../hooks/useModalOpenerForMyPaOrders";
import { useMyPaOrdersTableData } from "../../hooks/useMyPaOrdersTableData";
import { useNextPageDataFetch } from "../../hooks/useNextPageDataFetch";
import { getFilterCreationConfigForMyPaOrders } from "../../utils/filterConfigForMyPaOrders";
import { getMyPaOrdersAlgoliaTableHeaders } from "../../utils/generateTableDataFromAlgoliaRecords";
import { handleNavigateToNextScreenFromMyPaTable } from "../../utils/handleNavigateToNextPageFromMyMedicalPaTable";

/**
 * Displays the "My PA Orders" worklist table backed by Postgres. Handles
 * initial data fetching, filter/search, infinite-scroll pagination, and
 * opens modals for RPA screenshots, document viewers, review details, and
 * (exception-flow orgs) the estimate-value radio-select modal backed by
 * `state.estimateOptions`. Exception-flow filter panel overrides match the
 * Medical PA Orders worklist.
 */
const MyMedicalPaOrdersTableFromAlgolia = () => {
  const dispatch = useDispatch<AppDispatch>();
  const abortController = useRef<AbortController | null>(null);
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { data: estimateOptions } = useSelector(
    (state: RootState) => state.estimateOptions,
  );

  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const handleReviewIconClick = useReviewIconClick();
  const orgId = getOrgIdForFetchExternalWorklist();

  // Adjust table headers based on enable_review_mode flag and org-specific overrides
  const adjustedTableHeaders = useMemo(() => {
    const baseHeaders = getMyPaOrdersAlgoliaTableHeaders(orgId);
    return adjustTableHeadersForReviewMode(
      baseHeaders,
      isHealthFacilityFeatureEnabled(
        singleHealthFacilityDetails,
        "enable_review_mode",
      ),
    );
  }, [singleHealthFacilityDetails, orgId]);
  const navigate = useNavigate();
  const {
    myMedicalPaOrdersState,
    setMyMedicalPaOrdersState,
    tableData,
    setTableData,
  } = useMyPaOrdersTableData();
  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(setMyMedicalPaOrdersState, setTableData);

  const { openBoStatusModal, saveBoStatus } = useBoStatusRadioSelect({
    tableData,
    estimateOptions,
    onUpdated: () => setInitialDataFetchingExecuted(false),
  });
  const {
    paginationResetCount,
    setSearchTerm,
    searchTerm,
    validFilters,
    filterPayload,
  } = useFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setMyMedicalPaOrdersState,
  );

  const { exportData, loading: exportingInProgress } = useExportTableData();

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm ?? "",
    myMedicalPaOrdersState,
    setMyMedicalPaOrdersState,
    setTableData,
  );

  const { openedModalId, metaData } = useModalOpenerForMyPaOrders();

  const { filterData, isPostgresFacetResultsLoading } =
    useFilterOptionsGeneratorFromAlgolia(
      getFilterCreationConfigForMyPaOrders(orgId),
    );

  const filterSectionsData = useMemo(
    () => applySunstateUrologyFilterOverrides(filterData, orgId),
    [filterData, orgId],
  );

  /** Re-fetches table data and refreshes sidebar facet counts via Postgres. */
  const handleRefreshButtonClick = () => {
    setInitialDataFetchingExecuted(false);
    refreshPostgresFacetCounts(dispatch);
  };

  return (
    <>
      <div className="h-full">
        <CustomTable
          tableHeaders={adjustedTableHeaders}
          tableData={tableData}
          tableName={TableNames.MY_PA_ORDERS_FROM_ALGOLIA}
          itemsPerPage={10}
          pagesPerView={4}
          count={tableData?.length ?? 0}
          totalCount={myMedicalPaOrdersState?.data?.total_count ?? 0}
          endIndexOfTable={setEndIndex}
          searchingText={(searchText: string) => {
            setSearchTerm(searchText);
          }}
          hideSearchBar={false}
          showInLineLoader={myMedicalPaOrdersState.showinLineLoader}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          isFetching={myMedicalPaOrdersState.loading}
          requestForPaginationReset={paginationResetCount}
          filterSectionsData={filterSectionsData as FilterSection[]}
          showFilterButton={
            !myMedicalPaOrdersState.loading && !isPostgresFacetResultsLoading
          }
          onFilterApplyClicked={(data: FilterValues) => {
            dispatch(setFilter(data));
          }}
          onRefreshButton={() => {
            handleRefreshButtonClick();
          }}
          showRefreshButton={true}
          isDisabled={
            myMedicalPaOrdersState.loading ||
            myMedicalPaOrdersState.showinLineLoader
          }
          onRowClick={(rowData: any) => {
            handleNavigateToNextScreenFromMyPaTable(
              rowData,
              user?.id ?? "",
              user?.email ?? "",
              swapUnderscoreHyphen("my-pa-orders"),
              navigate,
            );
          }}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusCell(
              id,
              dispatch,
              authStatusOptions,
              abortController,
            );
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handleDocUploadScreenshotViewForOrdersForNewModalDesign(
              id,
              dispatch,
              abortController,
            );
          }}
          onOverlappingIconClick={(icon_id: string, order_id: string) =>
            handleReviewIconClick(icon_id, order_id, abortController)
          }
          onSideModalClose={handleRefreshButtonClick}
          showExportButton={true}
          onExportButtonClick={() => {
            if (!filterPayload) return;
            exportData(
              { ...filterPayload, export: true },
              "my-pa-orders-export.csv",
            );
          }}
          exportingInProgress={exportingInProgress}
          shouldDisableUiSorting={true}
          onClickableBadgeClick={
            isExceptionFlowForUrologyOrg(orgId) ? openBoStatusModal : undefined
          }
          getRowTooltip={(row) => getMedicalPaOrderRowTooltip(row)}
        />
      </div>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
      {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
        <DocumentScreenshotViewerModal
          title={DOCUMENT_SCREENSHOT_VIEWER_TITLE}
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
      {openedModalId === ModalId.VIEW_REVIEW_MODAL && (
        <ViewReviewModal
          onClose={() => {
            dispatch(closeModal());
            abortController.current?.abort();
            abortController.current = null;
          }}
          reviewData={metaData?.reviewData ?? null}
          isLoading={metaData?.isLoading ?? false}
        />
      )}
      {openedModalId === ModalId.RADIO_SELECT_MODAL && (
        <RadioSelectModal
          onSave={saveBoStatus}
          onClose={() => dispatch(closeModal())}
          onCancel={() => dispatch(closeModal())}
        />
      )}
    </>
  );
};

export default MyMedicalPaOrdersTableFromAlgolia;
